import React, { createContext, useContext, useState, useEffect } from 'react';

export type LanguageCode = 'en' | 'hi' | 'es' | 'fr' | 'de' | 'ja';

export interface LanguageOption {
  code: LanguageCode;
  name: string;
  nativeName: string;
  flag: string;
}

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
];

export const TRANSLATIONS: Record<LanguageCode, Record<string, string>> = {
  en: {
    nav_home: 'Home',
    nav_about: 'About Us',
    nav_services: 'Services',
    nav_workflow: 'Workflow',
    nav_technologies: 'Technologies',
    nav_specs: 'Domains & Specs',
    nav_why_us: 'Why Us',
    nav_contact: 'Contact',
    nav_blog: 'Blog',
    nav_faq: 'FAQ',
    nav_careers: 'Careers',
    btn_get_started: 'Get Started',
    btn_estimator: 'Calculate Cost',
    btn_freelance_openings: 'Freelance Hiring Openings',
    btn_explore_services: 'Explore Services',
    btn_contact_experts: 'Contact Experts',
    btn_request_quote: 'Request Free Quote',
    hero_badge: 'AI-Driven Publishing Solutions',
    hero_title_1: 'Transforming Publishing',
    hero_title_2: 'Through AI & Creative Excellence',
    hero_subtitle: 'Transforming manuscripts into JATS XML, PDF/UA, MathML, and EPUB3 with high efficiency, automated quality controls, and WCAG 2.1 AA accessibility compliance.',
    footer_rights: 'All rights reserved.',
    admin_login: 'Admin Portal',
    select_language: 'Language',
    stats_delivery: 'Rapid Processing',
    stats_accuracy: 'Automated Accuracy',
    stats_compliance: 'WCAG & JATS Compliant'
  },
  hi: {
    nav_home: 'मुख्य पृष्ठ',
    nav_about: 'हमारे बारे में',
    nav_services: 'सेवाएं',
    nav_workflow: 'कार्यप्रणाली',
    nav_technologies: 'प्रौद्योगिकियां',
    nav_specs: 'क्षेत्र एवं विनिर्देश',
    nav_why_us: 'हमें क्यों चुनें',
    nav_contact: 'संपर्क करें',
    nav_blog: 'ब्लॉग',
    nav_faq: 'सामान्य प्रश्न',
    nav_careers: 'करियर',
    btn_get_started: 'शुरू करें',
    btn_estimator: 'लागत का अनुमान लगाएं',
    btn_freelance_openings: 'फ्रीलांस भर्ती खुली है',
    btn_explore_services: 'सेवाएं देखें',
    btn_contact_experts: 'विशेषज्ञों से संपर्क करें',
    btn_request_quote: 'मुफ्त कोटेशन पाएं',
    hero_badge: 'अग्रणी अकादमिक एवं एसटीएम प्रकाशन समाधान',
    hero_title_1: 'एंटरप्राइज',
    hero_title_2: 'अकादमिक और एसटीएम पब्लिशिंग',
    hero_subtitle: 'पांडुलिपियों को JATS XML, PDF/UA, MathML, और EPUB3 में उच्च दक्षता और WCAG 2.1 AA अनुपालन के साथ परिवर्तित करना।',
    footer_rights: 'सर्वाधिकार सुरक्षित।',
    admin_login: 'एडमिन पोर्टल',
    select_language: 'भाषा चुनें',
    stats_delivery: 'त्वरित प्रसंस्करण',
    stats_accuracy: 'स्वचालित सटीकता',
    stats_compliance: 'WCAG एवं JATS अनुपालित'
  },
  es: {
    nav_home: 'Inicio',
    nav_about: 'Sobre Nosotros',
    nav_services: 'Servicios',
    nav_workflow: 'Flujo de Trabajo',
    nav_technologies: 'Tecnologías',
    nav_specs: 'Dominios y Especificaciones',
    nav_why_us: 'Por Qué Nosotros',
    nav_contact: 'Contacto',
    nav_blog: 'Blog',
    nav_faq: 'Preguntas Frecuentes',
    nav_careers: 'Carreras',
    btn_get_started: 'Empezar',
    btn_estimator: 'Calcular Costo',
    btn_freelance_openings: 'Vacantes Freelance Abiertas',
    btn_explore_services: 'Explorar Servicios',
    btn_contact_experts: 'Contactar Expertos',
    btn_request_quote: 'Solicitar Cotización Gratis',
    hero_badge: 'Soluciones Líderes en Publicación Académica y STM',
    hero_title_1: 'Empresarial',
    hero_title_2: 'Publicación Académica y STM',
    hero_subtitle: 'Transformando manuscritos en JATS XML, PDF/UA, MathML y EPUB3 con alta eficiencia y cumplimiento de accesibilidad WCAG 2.1 AA.',
    footer_rights: 'Todos los derechos reservados.',
    admin_login: 'Portal de Admin',
    select_language: 'Idioma',
    stats_delivery: 'Procesamiento Rápido',
    stats_accuracy: 'Precisión Automatizada',
    stats_compliance: 'Cumple con WCAG y JATS'
  },
  fr: {
    nav_home: 'Accueil',
    nav_about: 'À Propos',
    nav_services: 'Services',
    nav_workflow: 'Flux de Travail',
    nav_technologies: 'Technologies',
    nav_specs: 'Domaines et Spécifications',
    nav_why_us: 'Pourquoi Nous',
    nav_contact: 'Contact',
    nav_blog: 'Blog',
    nav_faq: 'FAQ',
    nav_careers: 'Carrières',
    btn_get_started: 'Commencer',
    btn_estimator: 'Calculer le Coût',
    btn_freelance_openings: 'Recrutement Freelance Ouvert',
    btn_explore_services: 'Explorer les Services',
    btn_contact_experts: 'Contacter les Experts',
    btn_request_quote: 'Demander un Devis Gratuit',
    hero_badge: 'Solutions d’Édition Académique et STM de Premier Plan',
    hero_title_1: 'Entreprise',
    hero_title_2: 'Édition Académique et STM',
    hero_subtitle: 'Transformation de manuscrits en JATS XML, PDF/UA, MathML et EPUB3 avec une grande efficacité et conformité WCAG 2.1 AA.',
    footer_rights: 'Tous droits réservés.',
    admin_login: 'Portail Admin',
    select_language: 'Langue',
    stats_delivery: 'Traitement Rapide',
    stats_accuracy: 'Précision Automatisée',
    stats_compliance: 'Conforme WCAG et JATS'
  },
  de: {
    nav_home: 'Startseite',
    nav_about: 'Über Uns',
    nav_services: 'Dienstleistungen',
    nav_workflow: 'Arbeitsablauf',
    nav_technologies: 'Technologien',
    nav_specs: 'Domänen & Spezifikationen',
    nav_why_us: 'Warum Wir',
    nav_contact: 'Kontakt',
    nav_blog: 'Blog',
    nav_faq: 'Häufige Fragen',
    nav_careers: 'Karriere',
    btn_get_started: 'Jetzt Starten',
    btn_estimator: 'Kosten Berechnen',
    btn_freelance_openings: 'Freiberufliche Stellen',
    btn_explore_services: 'Dienstleistungen Erkunden',
    btn_contact_experts: 'Experten Kontaktieren',
    btn_request_quote: 'Kostenloses Angebot',
    hero_badge: 'Führende Lösungen für Akademisches & STM-Verlagswesen',
    hero_title_1: 'Enterprise',
    hero_title_2: 'Akademisches & STM Publishing',
    hero_subtitle: 'Umwandlung von Manuskripten in JATS XML, PDF/UA, MathML und EPUB3 mit hoher Effizienz und WCAG 2.1 AA Barrierefreiheit.',
    footer_rights: 'Alle Rechte vorbehalten.',
    admin_login: 'Admin-Portal',
    select_language: 'Sprache',
    stats_delivery: 'Schnelle Verarbeitung',
    stats_accuracy: 'Automatisierte Präzision',
    stats_compliance: 'WCAG & JATS Konform'
  },
  ja: {
    nav_home: 'ホーム',
    nav_about: '会社概要',
    nav_services: 'サービス',
    nav_workflow: 'ワークフロー',
    nav_technologies: 'テクノロジー',
    nav_specs: 'ドメインと仕様',
    nav_why_us: '当社が選ばれる理由',
    nav_contact: 'お問い合わせ',
    nav_blog: 'ブログ',
    nav_faq: 'よくある質問',
    nav_careers: '採用情報',
    btn_get_started: '始める',
    btn_estimator: '費用計算',
    btn_freelance_openings: 'フリーランス募集',
    btn_explore_services: 'サービスを見る',
    btn_contact_experts: '専門家に相談',
    btn_request_quote: '無料見積もり',
    hero_badge: '最先端の学術・STM出版ソリューション',
    hero_title_1: 'エンタープライズ',
    hero_title_2: '学術・STM出版',
    hero_subtitle: '原稿をJATS XML、PDF/UA、MathML、EPUB3に高効率かつWCAG 2.1 AAアクセシビリティ準拠で変換します。',
    footer_rights: 'All rights reserved.',
    admin_login: '管理者ポータル',
    select_language: '言語選択',
    stats_delivery: '高速処理',
    stats_accuracy: '自動高精度',
    stats_compliance: 'WCAG & JATS 適合'
  }
};

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  t: (key: string, fallback?: string) => string;
  currentLanguageOption: LanguageOption;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    const saved = localStorage.getItem('asap_language') as LanguageCode;
    if (saved && TRANSLATIONS[saved]) {
      return saved;
    }
    return 'en';
  });

  const setLanguage = (lang: LanguageCode) => {
    if (TRANSLATIONS[lang]) {
      setLanguageState(lang);
      localStorage.setItem('asap_language', lang);
      document.documentElement.lang = lang;
    }
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = (key: string, fallback?: string): string => {
    const langDict = TRANSLATIONS[language];
    if (langDict && langDict[key]) {
      return langDict[key];
    }
    const enDict = TRANSLATIONS['en'];
    if (enDict && enDict[key]) {
      return enDict[key];
    }
    return fallback || key;
  };

  const currentLanguageOption = SUPPORTED_LANGUAGES.find(l => l.code === language) || SUPPORTED_LANGUAGES[0];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, currentLanguageOption }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
